import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { resetVideo, uploadVideo } from "../store/action";
import {
  ClientPayloadProps,
  CreateVideoError,
  CreateVideoInput,
  UploadVideoInput,
  VideoProps,
} from "../store/actionType";
import { ApiError } from "components";
import VideoForm from "./components/VideoForm";
import { showSnackbar } from "utils/snack_bar/store/action";
import { AppDispatch } from "index";

interface OwnProps {
  videoData: VideoProps;
  closeModal: () => void;
  posterUrl: string | null;
}

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = DispatchProps & OwnProps;

const HandleUploadVideo: React.FC<Props> = ({
  closeModal,
  videoData,
  onUploadVideo,
  onResetVideo,
  posterUrl,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateVideoInput>({
    title: videoData?.video?.title,
    episode_id: videoData?.video?.episode?.id.toString(),
    category_id: videoData?.video?.category?.id.toString(),
    season_folder_id: videoData?.video?.season_folder_id?.toString(),
  });
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [error, setError] = useState<CreateVideoError>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [readyToUpload, setReadyToUpload] = useState(false);
  const videoDetailsState = useSelector(
    (state: any) => state.videoDetails?.genericVideoDetailsState
  );
  const uploadVideoState = useSelector(
    (state: any) => state.videoDetails?.videoDetailsState
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleSave = async (e: FormEvent) => {
    e.preventDefault();

    if (formData.title.trim() === "") {
      setError({ title: "Video title is required" });
      return;
    }

    if (!videoFile) {
      setError({ video: "Video file is required" });
      return;
    }

    setError({});
    setLoading(true);

    try {
      const input: UploadVideoInput = {
        title: videoData?.video?.title || formData.title,
        folder_id: videoData?.video?.season_folder_id?.toString(),
        create_video_id: videoData?.video?.id.toString(),
      };
      await onUploadVideo(input);

      setSubmitted(true);
    } catch (err: any) {
      setApiError("Failed to create video metadata");
      console.error(err);
    } finally {
      setLoading(false)
    }
  };

  useEffect(() => {
    if (submitted ) {
      setReadyToUpload(true);
    }
  }, [submitted]);

  useEffect(() => {
    if (!videoDetailsState.isLoading && submitted) {
      if (videoDetailsState.error) {
        setApiError(videoDetailsState.error);
      } else {
        setApiError(null);
        // closeModal();
      }
    }
  }, [videoDetailsState.isLoading, videoDetailsState.error, submitted]);

  useEffect(() => {
    const uploadVideo = async () => {
      const uploadUrl: ClientPayloadProps = uploadVideoState?.upload_Video;
      const url = uploadUrl?.uploadLink;
      // console.warn("Missing URL or video file");
      if (!url || !videoFile) return;

      const formDataUpload = new FormData();
      formDataUpload.append("key", uploadUrl.key);
      formDataUpload.append("success_action_status", "201");
      formDataUpload.append("success_action_redirect", "");
      formDataUpload.append("policy", uploadUrl.policy);
      formDataUpload.append("x-amz-algorithm", uploadUrl.x_amz_algorithm);
      formDataUpload.append("x-amz-credential", uploadUrl.x_amz_credential);
      formDataUpload.append("x-amz-date", uploadUrl.x_amz_date);
      formDataUpload.append("x-amz-signature", uploadUrl.x_amz_signature);
      formDataUpload.append("file", videoFile);

      try {
        const response = await fetch(url, {
          method: "POST",
          body: formDataUpload,
        });
        if (!response.ok) {
          throw new Error("video upload failed");
        }

        dispatch(showSnackbar("Video uploaded successfully!"));
        onResetVideo();
        closeModal();
      } catch (err: any) {
        console.error("Upload error", err);
        setApiError("Video upload failed: " + err.message);
      }
    };

    if (readyToUpload) {
      uploadVideo();
      setReadyToUpload(false);
    }
  }, [uploadVideoState?.upload_Video, videoFile, readyToUpload]);

  return (
    <>
      <VideoForm
        posterUrl={posterUrl}
        handleChange={handleChange}
        handleSubmit={handleSave}
        formData={formData}
        videoError={error}
        loading={loading}
        header={"Upload Video"}
        handleVideoFileChange={setVideoFile}
        isNewVideo
      />
      {apiError && <ApiError>{apiError}</ApiError>}
    </>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  onUploadVideo: (input: UploadVideoInput) => dispatch(uploadVideo(input)),
  onResetVideo: () => dispatch(resetVideo()),
});

export default connect(null, mapDispatchToProps)(HandleUploadVideo);
